<script setup lang="ts">
import {
  MANUAL_REGISTRATION_API_URL,
  MANUAL_REGISTRATION_MOCK_ENABLED,
  REGISTRATION_FIELDS,
  type ManualRegistration,
  type ManualRegistrationResponse,
  type RegistrationFieldName,
} from '../lib/manualRegistration';
import { customFetch } from '~/shared/lib/customFetch';
import { isValidEmail } from '~/shared/utils/isValidEmail';

const props = defineProps<{
  eventId: number;
  eventName: string;
  isVisible: boolean;
}>();

const emit = defineEmits<{ close: [] }>();
const form = reactive<Record<RegistrationFieldName, string>>({
  last_name: '',
  first_name: '',
  company: '',
  position: '',
  business_field: '',
  phone: '',
  email: '',
  telegram_nick: '',
});
const promoCode = ref('');
const consent = ref(false);
const isSubmitting = ref(false);
const isSuccess = ref(false);
const errorMessage = ref('');
const invalidFields = ref<RegistrationFieldName[]>([]);
const registrationForm = useTemplateRef<HTMLFormElement>('registrationForm');
const captcha = useTemplateRef('captcha');
const captchaToken = ref('');
const CAPTCHA_ERROR = 'Подтвердите, что вы не робот';

watch(captchaToken, (token) => {
  if (token && errorMessage.value === CAPTCHA_ERROR) errorMessage.value = '';
});

watch(
  () => props.isVisible,
  (isVisible) => {
    if (!isVisible) {
      captchaToken.value = '';
      captcha.value?.reset();
    }
  },
);

const handleFieldInput = (name: RegistrationFieldName) => {
  invalidFields.value = invalidFields.value.filter((field) => field !== name);
  errorMessage.value = '';
};

watch(
  () => props.eventId,
  () => {
    for (const field of REGISTRATION_FIELDS) form[field.name] = '';
    promoCode.value = '';
    consent.value = false;
    isSuccess.value = false;
    errorMessage.value = '';
    invalidFields.value = [];
    captchaToken.value = '';
    captcha.value?.reset();
  },
);

const submit = async () => {
  if (isSubmitting.value || isSuccess.value) return;
  errorMessage.value = '';

  const emptyFields = REGISTRATION_FIELDS.filter(
    (field) => !form[field.name].trim(),
  ).map((field) => field.name);

  if (emptyFields.length) {
    invalidFields.value = emptyFields;
    errorMessage.value = 'Заполните обязательные поля';
    return;
  }

  if (!isValidEmail(form.email)) {
    invalidFields.value = ['email'];
    errorMessage.value = 'Проверьте адрес электронной почты';
    return;
  }

  if (form.phone.replace(/\D/g, '').length < 10) {
    invalidFields.value = ['phone'];
    errorMessage.value = 'Проверьте номер телефона';
    return;
  }

  invalidFields.value = [];

  if (!consent.value) {
    errorMessage.value =
      'Подтвердите согласие на обработку персональных данных';
    return;
  }

  if (!captchaToken.value) {
    errorMessage.value = CAPTCHA_ERROR;
    return;
  }

  isSubmitting.value = true;
  try {
    const body: ManualRegistration = {
      ...form,
      last_name: form.last_name.trim(),
      first_name: form.first_name.trim(),
      event: props.eventId,
      promo_code: promoCode.value,
      captcha_token: captchaToken.value,
    };
    if (!MANUAL_REGISTRATION_MOCK_ENABLED) {
      await customFetch<ManualRegistrationResponse>(
        MANUAL_REGISTRATION_API_URL,
        {
          method: 'POST',
          body,
          retry: 0,
        },
      );
    }
    isSuccess.value = true;
    for (const field of REGISTRATION_FIELDS) form[field.name] = '';
    promoCode.value = '';
    consent.value = false;
    captchaToken.value = '';
  } catch (error) {
    const captchaErrors = (
      error as { data?: { captcha_token?: unknown } } | null
    )?.data?.captcha_token;
    errorMessage.value = Array.isArray(captchaErrors)
      ? CAPTCHA_ERROR
      : 'Не удалось отправить заявку. Попробуйте ещё раз или свяжитесь с организатором: l.mikhailova@cre.ru.';
    // Токен одноразовый и после запроса может быть уже использован.
    captchaToken.value = '';
    captcha.value?.reset();
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Modal
    :is-visible="isVisible"
    :title="`Регистрация на ${eventName}`"
    hide-close
    @close="emit('close')"
  >
    <section :class="$style.panel">
      <button
        type="button"
        :class="$style.close"
        aria-label="Закрыть"
        @click="emit('close')"
      >
        <Icon name="my-icon:i-close" />
      </button>
      <h2 :class="$style.title">
        {{ isSuccess ? 'Спасибо!' : 'Регистрация на мероприятие' }}
      </h2>
      <p :class="$style.eventName">{{ eventName }}</p>
      <div v-if="isSuccess" role="status" :class="$style.success">
        <p>
          Мы получили вашу заявку. Организатор свяжется с вами для подтверждения
          участия.
        </p>
        <Button
          theme="primary"
          size="xl"
          :class="$style.submit"
          @click="emit('close')"
        >
          Закрыть
        </Button>
      </div>
      <form
        v-else
        ref="registrationForm"
        :class="$style.form"
        novalidate
        @submit.prevent="submit"
      >
        <fieldset :class="$style.fields" :disabled="isSubmitting">
          <label
            v-for="field in REGISTRATION_FIELDS"
            :key="field.name"
            :class="$style.field"
          >
            <span :class="$style.fieldLabel"
              >{{ field.label }} <span :class="$style.required">*</span></span
            >
            <input
              v-model.trim="form[field.name]"
              :name="field.name"
              :type="field.type"
              :autocomplete="field.autocomplete"
              :placeholder="`${field.label}*`"
              :class="{
                [$style.fieldInvalid!]: invalidFields.includes(field.name),
              }"
              :aria-invalid="invalidFields.includes(field.name)"
              :maxlength="
                field.type === 'tel' ? 30 : field.type === 'email' ? 254 : 255
              "
              required
              @input="handleFieldInput(field.name)"
            />
          </label>
          <label :class="$style.field">
            <span :class="$style.fieldLabel">Промокод</span>
            <input
              v-model.trim="promoCode"
              name="promo_code"
              type="text"
              placeholder="Промокод"
              maxlength="100"
              @input="errorMessage = ''"
            />
          </label>
          <p :class="$style.note">* поля обязательны для заполнения</p>
          <SmartCaptcha
            v-if="isVisible"
            ref="captcha"
            v-model="captchaToken"
            :class="$style.captcha"
          />
          <Checkbox
            v-model="consent"
            :disabled="isSubmitting"
            :class="$style.consent"
          >
            Принимаю условия
            <a
              href="https://cre.ru/privacy_policy"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
              >Политики по защите персональных данных</a
            >
            и даю согласие на обработку моих персональных данных
          </Checkbox>
        </fieldset>
        <p v-if="errorMessage" role="alert" :class="$style.error">
          {{ errorMessage }}
        </p>
        <button
          type="submit"
          hidden
          :class="$style.hiddenSubmit"
          :disabled="isSubmitting || !consent"
        />
        <Button
          theme="primary"
          size="xl"
          :class="$style.submit"
          :disabled="!consent"
          :is-loading="isSubmitting"
          role="button"
          tabindex="0"
          @click="registrationForm?.requestSubmit()"
          @keydown.enter.prevent="registrationForm?.requestSubmit()"
          @keydown.space.prevent="registrationForm?.requestSubmit()"
        >
          <span :class="$style.submitLabel">
            Отправить
            <Icon name="my-icon:i-arrow-right" :class="$style.icon" />
          </span>
        </Button>
      </form>
    </section>
  </Modal>
</template>

<style module lang="scss">
.panel {
  position: relative;
  width: min(840px, 90vw);
  max-height: 90vh;
  margin: 0 auto;
  overflow-y: auto;
  padding: var(--space-5x);
  background: var(--color-white);
  color: var(--color-black-1);
  @include text_body1;
}

.title {
  margin: 0 24px 12px 0;
  @include text_h4;
}

.eventName {
  margin: 0 0 24px;
}

.form,
.success {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-2-5x);
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.note {
  grid-column: 1 / -1;
  margin: 0;
  color: var(--color-gray-text);
  font-size: 14px;
}

.field {
  display: block;

  input {
    width: 100%;
    height: 60px;
    padding: var(--space-2x);
    border: 1px solid var(--color-gray-line);
    border-radius: 0;
    background: var(--color-white);
    color: inherit;
    font: inherit;

    &.fieldInvalid {
      border-color: var(--color-red-2);
    }
  }
}

.fieldLabel {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

.required {
  color: var(--color-red-1);
}

.error {
  margin: 0;
  @include text_body2;
  color: var(--color-red-2);
}

.hiddenSubmit {
  display: none;
}

.consent {
  grid-column: 1 / -1;
}

.captcha {
  grid-column: 1 / -1;
}

.submit {
  align-self: flex-start;
}

.submitLabel {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-1-5x);
  width: 100%;
  @include text_button1;
}

.icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  path {
    fill: currentColor;
  }
}

.close {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

@media (max-width: 899px) {
  .panel {
    width: 100%;
    padding: 24px 20px;
  }

  .fields {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--space-1-5x);
  }

  .submit {
    width: 100%;
  }
}
</style>
